import { supabase } from '@/lib/supabase/client';
import { getClientIP } from '@/utils/clientIP';
import { debugLog, debugError } from '@/utils/debugLogger';

export type ConsentType = 
  | 'PRIVACY_POLICY' 
  | 'MARKETING' 
  | 'DATA_EXPORT' 
  | 'CONTACT_FORM'
  | 'PRICE_LOCK_REQUEST'
  | 'DIAMOND_VALUATION_REQUEST'
  | 'PRODUCT_PRICE_LOCK_REQUEST'
  | 'DEALER_RATING'
  | 'DEALER_REPORT'
  | 'BUSINESS_REGISTRATION'
  | 'VAULT_AI_ESTIMATION'
  | 'VAULT_DOCUMENT_UPLOAD'
  | 'VAULT_EXPERT_REQUEST'
  | 'DEALER_TERMS'
  | 'DEALER_CLAIM'
  | 'PRICE_ALERT';

export interface ConsentLogInput {
  consentType: ConsentType;
  granted: boolean;
  source: string;
  userId?: string | null;
}

/**
 * Registra un consenso nei consent_logs per audit GDPR
 * Cattura automaticamente IP address e user agent
 * 
 * @param input - Dati del consenso da registrare
 * @returns Promise<boolean> - true se registrato con successo
 */
export async function logConsent(input: ConsentLogInput): Promise<boolean> {
  try {
    const ipAddress = await getClientIP();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    
    const { error } = await supabase.from('consent_logs').insert({
      user_id: input.userId || null,
      consent_type: input.consentType,
      granted: input.granted,
      source: input.source,
      ip_address: ipAddress,
      user_agent: userAgent,
    });
    
    if (error) {
      debugError('api', 'Failed to log consent:', error, 'ConsentService');
      return false;
    }
    
    debugLog('api', `Consent logged: ${input.consentType} (${input.granted ? 'granted' : 'revoked'})`, undefined, 'ConsentService');
    return true;
  } catch (error) {
    debugError('api', 'Error logging consent:', error, 'ConsentService');
    return false;
  }
}

/**
 * Registra il consenso privacy per form anonimi (contact form, etc.)
 * Wrapper convenience per logConsent
 */
export async function logContactFormConsent(source: string): Promise<boolean> {
  return logConsent({
    consentType: 'CONTACT_FORM',
    granted: true,
    source,
  });
}

/**
 * Registra il consenso per richieste di blocco prezzo
 */
export async function logPriceLockConsent(userId?: string | null): Promise<boolean> {
  return logConsent({
    consentType: 'PRICE_LOCK_REQUEST',
    granted: true,
    source: 'price_lock_modal',
    userId,
  });
}

/**
 * Registra il consenso per richieste di valutazione diamanti
 */
export async function logDiamondValuationConsent(userId?: string | null): Promise<boolean> {
  return logConsent({
    consentType: 'DIAMOND_VALUATION_REQUEST',
    granted: true,
    source: 'diamond_valuation_modal',
    userId,
  });
}

/**
 * Registra il consenso per blocco prezzo prodotto (monete/lingotti)
 */
export async function logProductPriceLockConsent(userId?: string | null): Promise<boolean> {
  return logConsent({
    consentType: 'PRODUCT_PRICE_LOCK_REQUEST',
    granted: true,
    source: 'product_price_lock_modal',
    userId,
  });
}

/**
 * Registra il consenso per recensione dealer
 */
export async function logDealerRatingConsent(userId?: string | null): Promise<boolean> {
  return logConsent({
    consentType: 'DEALER_RATING',
    granted: true,
    source: 'dealer_rating_modal',
    userId,
  });
}

/**
 * Registra il consenso per segnalazione dealer
 */
export async function logDealerReportConsent(userId?: string | null): Promise<boolean> {
  return logConsent({
    consentType: 'DEALER_REPORT',
    granted: true,
    source: 'dealer_report_modal',
    userId,
  });
}

/**
 * Registra il consenso per registrazione attività business
 */
export async function logBusinessRegistrationConsent(userId?: string | null): Promise<boolean> {
  return logConsent({
    consentType: 'BUSINESS_REGISTRATION',
    granted: true,
    source: 'business_registration_form',
    userId,
  });
}

/**
 * Registra il consenso per stima AI nel Vault
 */
export async function logVaultAIEstimationConsent(userId: string): Promise<boolean> {
  return logConsent({
    consentType: 'VAULT_AI_ESTIMATION',
    granted: true,
    source: 'vault_ai_estimation',
    userId,
  });
}

/**
 * Registra il consenso per upload documenti nel Vault
 */
export async function logVaultDocumentUploadConsent(userId: string): Promise<boolean> {
  return logConsent({
    consentType: 'VAULT_DOCUMENT_UPLOAD',
    granted: true,
    source: 'vault_document_upload',
    userId,
  });
}

/**
 * Registra il consenso per richiesta validazione esperto nel Vault
 */
export async function logVaultExpertRequestConsent(userId: string): Promise<boolean> {
  return logConsent({
    consentType: 'VAULT_EXPERT_REQUEST',
    granted: true,
    source: 'vault_expert_request',
    userId,
  });
}

/**
 * Registra il consenso per claim dealer (verifica OTP email)
 */
export async function logDealerClaimConsent(userId?: string | null): Promise<boolean> {
  return logConsent({
    consentType: 'DEALER_CLAIM',
    granted: true,
    source: 'dealer_claim_otp',
    userId,
  });
}

/**
 * Registra il consenso per creazione avviso di prezzo
 */
export async function logPriceAlertConsent(userId?: string | null): Promise<boolean> {
  return logConsent({
    consentType: 'PRICE_ALERT',
    granted: true,
    source: 'price_alert_form',
    userId,
  });
}
